import type { ReactNode } from 'react';

export enum AppId {
	Portal = 'portal',
	Creator = 'creator',
	VibeBuilder = 'vibe-builder'
}

export enum OrganizationType {
	Customer = 'customer',
	Partner = 'partner'
}

export enum OrganizationScope {
	None = 'none',
	Required = 'required'
}

export enum NavigationIntent {
	AccountRoot = 'account.root',
	PartnerSignup = 'partner.signup',
	AuthLogin = 'auth.login',
	AuthCallback = 'auth.callback',
	AuthOAuth = 'auth.oauth',
	AuthSsoCallback = 'auth.ssoCallback',
	AuthConnect = 'auth.connect',
	CustomerDashboard = 'customer.dashboard',
	CustomerContacts = 'customer.contacts',
	CustomerSessions = 'customer.sessions',
	CustomerSessionDetails = 'customer.sessionDetails',
	CustomerRecording = 'customer.recording',
	CustomerSettings = 'customer.settings',
	CustomerTeam = 'customer.team',
	CustomerProfile = 'customer.profile',
	CustomerBilling = 'customer.billing',
	CustomerWidgets = 'customer.widgets',
	CustomerVibeBuilder = 'customer.vibeBuilder',
	CustomerAgentConfiguration = 'customer.agentConfiguration',
	PartnerDashboard = 'partner.dashboard',
	PartnerCustomers = 'partner.customers',
	PartnerEmployees = 'partner.employees',
	PartnerContracts = 'partner.contracts',
	PartnerTransactions = 'partner.transactions',
	PartnerTeam = 'partner.team',
	PartnerProfile = 'partner.profile',
	PartnerBilling = 'partner.billing',
	PartnerProducts = 'partner.products',
	PartnerCustomerQuotas = 'partner.customerQuotas',
	CreatorAgentCreation = 'creator.agentCreation',
	PublicSessionDetails = 'public.sessionDetails',
	PublicRecording = 'public.recording',
	ScopedNotFound = 'portal.scopedNotFound'
}

export type RouteParams = {
	organizationIdn?: string;
	organizationType?: OrganizationType;
	recordId?: string;
	sessionId?: string;
	[key: string]: OrganizationType | string | undefined;
};

export type QueryPrimitive = string | number | boolean;

export type QueryParams = Record<
	string,
	QueryPrimitive | readonly QueryPrimitive[] | null | undefined
>;

export type ResolvedRoute = {
	appId: AppId;
	intent: NavigationIntent;
	params: RouteParams;
	path: string;
	query: QueryParams;
	requiresAuth: boolean;
	url: string;
};

export type NavigationContext = Readonly<Record<string, unknown>>;

export type NavigationRequest = {
	context?: NavigationContext;
	intent: NavigationIntent;
	params?: RouteParams;
	query?: QueryParams;
	replace?: boolean;
};

export type ExternalNavigationOptions = {
	features?: string;
	replace?: boolean;
	target?: '_blank' | '_parent' | '_self' | '_top';
};

export type ExternalTab = {
	close: () => void;
	navigate: (url: string) => void;
};

export type NavigationLifecycleEvent = {
	context?: NavigationContext;
	currentRoute: ResolvedRoute | null;
	targetRoute: ResolvedRoute;
};

export type NavigationGuard = (
	event: NavigationLifecycleEvent
) => boolean | undefined;

export type RoutingClientLifecycleConfig = {
	onAfterNavigate?: (event: NavigationLifecycleEvent) => void;
	onBeforeNavigate?: (event: NavigationLifecycleEvent) => void;
};

export type RegisteredApp = {
	appId: AppId;
	isHost?: boolean;
};

export type BaseRouteDefinition = {
	appId: AppId;
	intent: NavigationIntent;
	matchPaths?: readonly string[];
	requiresAuth?: boolean;
};

export type RouteDefinition = BaseRouteDefinition &
	(
		| {
				buildPath?: (params: RouteParams) => string;
				organizationScope: OrganizationScope.None;
				organizationType?: never;
		  }
		| {
				buildPath?: (params: RouteParams) => string;
				organizationScope: OrganizationScope.Required;
				organizationType: OrganizationType | null;
		  }
	);

export type RouteContentProps = {
	element: ReactNode;
};

export type RouteElementProps = RouteContentProps & {
	intent: NavigationIntent;
};

export type RouterProps = {
	children: ReactNode;
};

export type RoutingClientBootstrapConfig = {
	apps: readonly RegisteredApp[];
	authCallbackOverrideUrl?: string | null;
	externalAppOrigins?: Partial<Record<AppId, string>>;
};
